import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  type ActivityResponse,
  ActivityResponseSchema,
  type CatchUpTimelineEvent,
  CatchUpTimelineEventCollectionSchema,
  type CatchUpTimelineEventCollection,
  CatchUpTimelineEventSchema,
  type CatchUpTimelineSummary,
  CatchUpTimelineSummarySchema,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/activity'
import {
  type AdditionalInfo,
  AdditionalInfoSchema,
  type IssueEventInfo,
  type PullRequestReviewInfo,
  type TimelineItem as ProtoTimelineItem,
  TimelineItemSchema,
  TimelineItemType,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import type {ActivityPayload, TimelineSummary, TimelineUser} from '../../../routes/route-payload-types'
import type {
  IssueEventItem,
  PullRequestReviewItem,
  PullRequestReviewState,
  TimelineItem,
} from '../../../page-data/payloads/timeline'

/**
 * Legacy (flag-off) summary event shape: a timestamp, an optional user, and a
 * free-form `additionalInfo` blob. Used only by the legacy→proto shim.
 */
type LegacySummaryEvent = {
  timestamp: string
  user?: TimelineUser
  additionalInfo?: unknown
}

/**
 * Builds the typed {@link AdditionalInfo} oneof for a summary event from its
 * legacy flat `additionalInfo` blob. Catch-up summary events are the same
 * underlying loader events as the timeline, so each category maps onto the same
 * oneof arm the timeline uses.
 */
type SummaryInfoBuilder = (info: unknown) => AdditionalInfo

/**
 * Converts the legacy catch-up JSON ({@link ActivityPayload}) **up** to the
 * canonical proto {@link ActivityResponse}. TEMPORARY — exists only to support
 * the flag-off path until `activity_proto` is fully rolled out, after which the
 * endpoint always emits proto and this shim is deleted.
 */
export function legacyToProtoActivity(legacy: ActivityPayload): ActivityResponse {
  return create(ActivityResponseSchema, {
    summary: legacySummaryToProto(legacy.summary),
    timeline: legacy.timeline.map(legacyTimelineItemToProto),
  })
}

/**
 * Normalizes a catch-up response to the canonical proto shape: a proto-decoded
 * message (carrying `$typeName`) passes through untouched; legacy JSON is
 * converted up via {@link legacyToProtoActivity}.
 */
export function normalizeActivity(result: ActivityResponse | ActivityPayload): ActivityResponse {
  return '$typeName' in result ? result : legacyToProtoActivity(result)
}

function legacySummaryToProto(summary: TimelineSummary): CatchUpTimelineSummary {
  return create(CatchUpTimelineSummarySchema, {
    approvals: legacyCollectionToProto(summary.approvals, summaryReviewInfoToProto),
    changesRequested: legacyCollectionToProto(summary.changesRequested, summaryReviewInfoToProto),
    comments: legacyCollectionToProto(summary.comments, summaryReviewInfoToProto),
    issueComments: legacyCollectionToProto(summary.issueComments, summaryIssueCommentInfoToProto),
    commits: legacyCollectionToProto(summary.commits, summaryCommitInfoToProto),
    closed: legacyCollectionToProto(summary.closed, summaryIssueEventInfoToProto),
    reopened: legacyCollectionToProto(summary.reopened, summaryIssueEventInfoToProto),
    markedAsDraft: legacyCollectionToProto(summary.markedAsDraft, summaryIssueEventInfoToProto),
    readyForReview: legacyCollectionToProto(summary.readyForReview, summaryIssueEventInfoToProto),
    labeled: legacyCollectionToProto(summary.labeled, summaryIssueEventInfoToProto),
    reviewRequested: legacyCollectionToProto(summary.reviewRequested, summaryIssueEventInfoToProto),
    forcePushed: legacyCollectionToProto(summary.forcePushed, summaryIssueEventInfoToProto),
    merged: legacyCollectionToProto(summary.merged, summaryIssueEventInfoToProto),
    baseChanged: legacyCollectionToProto(summary.baseChanged, summaryIssueEventInfoToProto),
    descriptionUpdated: legacyCollectionToProto(summary.descriptionUpdated, undefined),
    total: summary.total,
  })
}

function legacyCollectionToProto(
  collection: {
    count: number
    events: LegacySummaryEvent[]
  },
  buildInfo: SummaryInfoBuilder | undefined,
): CatchUpTimelineEventCollection {
  return create(CatchUpTimelineEventCollectionSchema, {
    count: collection.count,
    events: collection.events.map(event => legacySummaryEventToProto(event, buildInfo)),
  })
}

function legacySummaryEventToProto(
  event: LegacySummaryEvent,
  buildInfo: SummaryInfoBuilder | undefined,
): CatchUpTimelineEvent {
  return create(CatchUpTimelineEventSchema, {
    timestamp: event.timestamp,
    user: event.user
      ? {id: BigInt(event.user.id), login: event.user.login, avatarUrl: event.user.avatarUrl}
      : undefined,
    additionalInfo: buildInfo && event.additionalInfo != null ? buildInfo(event.additionalInfo) : undefined,
  })
}

function summaryReviewInfoToProto(info: unknown): AdditionalInfo {
  // Summary review events carry only `{reviewId, commentCount}` (the rest of the
  // review metadata is collapsed away by categorization). `commentCount` is the
  // sole field the sidebar reads back, via `sumReviewComments`.
  const value = info as {reviewId: number; commentCount: number}
  return create(AdditionalInfoSchema, {
    info: {case: 'pullRequestReview', value: {reviewId: BigInt(value.reviewId), commentCount: value.commentCount}},
  })
}

function summaryIssueEventInfoToProto(info: unknown): AdditionalInfo {
  return create(AdditionalInfoSchema, {
    info: {case: 'issueEvent', value: legacyIssueEventInfoToProto(info as IssueEventItem['additionalInfo'])},
  })
}

function summaryIssueCommentInfoToProto(info: unknown): AdditionalInfo {
  return create(AdditionalInfoSchema, {
    info: {case: 'issueComment', value: {commentId: BigInt((info as {commentId: number}).commentId)}},
  })
}

function summaryCommitInfoToProto(info: unknown): AdditionalInfo {
  const value = info as {sha: string; baseSha?: string; shortMessageText?: string}
  return create(AdditionalInfoSchema, {
    info: {
      case: 'pullRequestCommit',
      value: {sha: value.sha, baseSha: value.baseSha, shortMessageText: value.shortMessageText},
    },
  })
}

function legacyTimelineItemToProto(item: TimelineItem): ProtoTimelineItem {
  const user = item.user
    ? {id: BigInt(item.user.id), displayLogin: item.user.displayLogin, avatarUrl: item.user.avatarUrl}
    : undefined
  const base = {id: item.id, timestamp: item.timestamp, user}

  switch (item.type) {
    case 'IssueComment':
      return create(TimelineItemSchema, {
        ...base,
        type: TimelineItemType.ISSUE_COMMENT,
        additionalInfo: item.additionalInfo
          ? {info: {case: 'issueComment', value: {commentId: BigInt(item.additionalInfo.commentId)}}}
          : undefined,
      })
    case 'IssueEvent':
      return create(TimelineItemSchema, {
        ...base,
        type: TimelineItemType.ISSUE_EVENT,
        additionalInfo: {info: {case: 'issueEvent', value: legacyIssueEventInfoToProto(item.additionalInfo)}},
      })
    case 'PullRequestReview':
      return create(TimelineItemSchema, {
        ...base,
        type: TimelineItemType.PULL_REQUEST_REVIEW,
        additionalInfo: {info: {case: 'pullRequestReview', value: legacyReviewInfoToProto(item.additionalInfo)}},
      })
    case 'PullRequestCommit':
      return create(TimelineItemSchema, {
        ...base,
        type: TimelineItemType.PULL_REQUEST_COMMIT,
        additionalInfo: {
          info: {
            case: 'pullRequestCommit',
            value: {
              sha: item.additionalInfo.sha,
              baseSha: item.additionalInfo.baseSha,
              shortMessageText: item.additionalInfo.shortMessageText,
            },
          },
        },
      })
    case 'PullRequestRevisionMarker':
      return create(TimelineItemSchema, {
        ...base,
        user: undefined,
        type: TimelineItemType.PULL_REQUEST_REVISION_MARKER,
        additionalInfo: item.additionalInfo
          ? {
              info: {
                case: 'pullRequestRevisionMarker',
                value: {lastSeenCommitOid: item.additionalInfo.lastSeenCommitOid},
              },
            }
          : undefined,
      })
    case 'CrossReference':
      return create(TimelineItemSchema, {...base, type: TimelineItemType.CROSS_REFERENCE})
    case 'PullRequestReviewThread':
      return create(TimelineItemSchema, {...base, type: TimelineItemType.PULL_REQUEST_REVIEW_THREAD})
    case 'IssueEdit':
      return create(TimelineItemSchema, {...base, type: TimelineItemType.ISSUE_EDIT})
  }
}

function legacyIssueEventInfoToProto(info: IssueEventItem['additionalInfo']) {
  return {
    eventType: info.eventType,
    label: info.label ? {name: info.label.name, color: info.label.color} : undefined,
    reviewerLogin: info.reviewerLogin,
    reviewerTeamSlug: info.reviewerTeamSlug,
    assigneeLogin: info.assigneeLogin,
    previousTitle: info.previousTitle,
    newTitle: info.newTitle,
    beforeCommitOid: info.beforeCommitOid,
    afterCommitOid: info.afterCommitOid,
    ref: info.ref,
    dismissalMessage: info.dismissalMessage ?? undefined,
    commitOid: info.commitOid,
    stateReason: info.stateReason ?? undefined,
    lockReason: info.lockReason ?? undefined,
  }
}

function legacyReviewInfoToProto(info: PullRequestReviewItem['additionalInfo']) {
  return {
    state: info.state,
    reviewId: BigInt(info.reviewId ?? 0),
    commentCount: info.commentCount,
    isAuthor: info.isAuthor,
    qualifiedReviewer: info.qualifiedReviewer,
    reviewerType: info.reviewerType ?? '',
    dismissedState: info.dismissedState ?? undefined,
  }
}

/**
 * Converts the canonical proto {@link ActivityResponse} into the
 * {@link ActivityPayload} view-model the React components render. This is a
 * permanent domain conversion (not a flag shim) and remains after rollout.
 */
export function protoActivityToLegacy(proto: ActivityResponse): ActivityPayload {
  return {
    summary: protoSummaryToLegacy(proto.summary),
    timeline: proto.timeline.map(protoTimelineItemToLegacy),
  }
}

// --- Summary (deprecated categorized rollup) ---------------------------------

function protoSummaryToLegacy(summary?: CatchUpTimelineSummary): TimelineSummary {
  // The frontend `TimelineSummary` intentionally omits `reviewComments` and
  // `baseChangeFailed`; the extra proto collections are simply ignored. Each
  // collection's events carry the shared typed `AdditionalInfo` oneof, narrowed
  // back to the legacy flat shape by {@link summaryAdditionalInfoToLegacy}.
  return {
    approvals: mapCollection(summary?.approvals),
    changesRequested: mapCollection(summary?.changesRequested),
    comments: mapCollection(summary?.comments),
    issueComments: mapCollection(summary?.issueComments),
    commits: mapCollection(summary?.commits),
    closed: mapCollection(summary?.closed),
    reopened: mapCollection(summary?.reopened),
    markedAsDraft: mapCollection(summary?.markedAsDraft),
    readyForReview: mapCollection(summary?.readyForReview),
    labeled: mapCollection(summary?.labeled),
    reviewRequested: mapCollection(summary?.reviewRequested),
    forcePushed: mapCollection(summary?.forcePushed),
    merged: mapCollection(summary?.merged),
    baseChanged: mapCollection(summary?.baseChanged),
    descriptionUpdated: mapCollection(summary?.descriptionUpdated),
    total: summary?.total ?? 0,
  } as TimelineSummary
}

function mapCollection(collection?: CatchUpTimelineEventCollection): {count: number; events: unknown[]} {
  if (!collection) return {count: 0, events: []}
  return {count: collection.count, events: collection.events.map(mapSummaryEvent)}
}

function mapSummaryEvent(event: CatchUpTimelineEvent): {
  timestamp: string
  user?: TimelineUser
  additionalInfo?: unknown
} {
  return {
    timestamp: event.timestamp,
    user: event.user
      ? {id: Number(event.user.id), login: event.user.login, avatarUrl: event.user.avatarUrl}
      : undefined,
    additionalInfo: summaryAdditionalInfoToLegacy(event.additionalInfo),
  }
}

// Narrows a summary event's shared `AdditionalInfo` oneof back to the legacy
// flat shape, mirroring the per-arm narrowing the timeline does. The arm is
// determined by `info.case`, so no category context is needed.
function summaryAdditionalInfoToLegacy(additionalInfo?: AdditionalInfo): unknown {
  const info = additionalInfo?.info
  switch (info?.case) {
    case 'issueComment':
      return {commentId: Number(info.value.commentId)}
    case 'issueEvent':
      return issueEventInfoToLegacy(info.value)
    case 'pullRequestReview':
      return {reviewId: Number(info.value.reviewId), commentCount: info.value.commentCount ?? 0}
    case 'pullRequestCommit':
      return {sha: info.value.sha ?? '', baseSha: info.value.baseSha, shortMessageText: info.value.shortMessageText}
    case 'pullRequestRevisionMarker':
      return {lastSeenCommitOid: info.value.lastSeenCommitOid}
    default:
      return undefined
  }
}

// --- Timeline (ordered list, shares timeline.proto's TimelineItem) -----------

function protoTimelineItemToLegacy(item: ProtoTimelineItem): TimelineItem {
  const user = item.user
    ? {id: Number(item.user.id), displayLogin: item.user.displayLogin, avatarUrl: item.user.avatarUrl}
    : null
  const base = {id: item.id, timestamp: item.timestamp, user}
  const info = item.additionalInfo?.info

  switch (item.type) {
    case TimelineItemType.ISSUE_COMMENT:
      return {
        ...base,
        type: 'IssueComment',
        additionalInfo: info?.case === 'issueComment' ? {commentId: Number(info.value.commentId)} : null,
      }
    case TimelineItemType.ISSUE_EVENT: {
      const v = info?.case === 'issueEvent' ? info.value : undefined
      return {...base, type: 'IssueEvent', additionalInfo: issueEventInfoToLegacy(v)}
    }
    case TimelineItemType.PULL_REQUEST_REVIEW: {
      const v = info?.case === 'pullRequestReview' ? info.value : undefined
      return {...base, type: 'PullRequestReview', additionalInfo: pullRequestReviewInfoToLegacy(v)}
    }
    case TimelineItemType.PULL_REQUEST_COMMIT: {
      const v = info?.case === 'pullRequestCommit' ? info.value : undefined
      return {
        ...base,
        type: 'PullRequestCommit',
        additionalInfo: {sha: v?.sha ?? '', baseSha: v?.baseSha, shortMessageText: v?.shortMessageText},
      }
    }
    case TimelineItemType.PULL_REQUEST_REVISION_MARKER: {
      const v = info?.case === 'pullRequestRevisionMarker' ? info.value : undefined
      return {
        ...base,
        user: null,
        type: 'PullRequestRevisionMarker',
        additionalInfo: v ? {lastSeenCommitOid: v.lastSeenCommitOid} : null,
      }
    }
    case TimelineItemType.CROSS_REFERENCE:
      return {...base, type: 'CrossReference', additionalInfo: null}
    case TimelineItemType.PULL_REQUEST_REVIEW_THREAD:
      return {...base, type: 'PullRequestReviewThread', additionalInfo: null}
    default:
      // ISSUE_EDIT and any unknown/unspecified type carry no metadata.
      return {...base, type: 'IssueEdit', additionalInfo: null}
  }
}

function issueEventInfoToLegacy(v?: IssueEventInfo) {
  return {
    eventType: v?.eventType ?? '',
    assigneeLogin: v?.assigneeLogin,
    label: v?.label ? {name: v.label.name, color: v.label.color} : undefined,
    reviewerLogin: v?.reviewerLogin,
    reviewerTeamSlug: v?.reviewerTeamSlug,
    dismissalMessage: v?.dismissalMessage,
    commitOid: v?.commitOid,
    beforeCommitOid: v?.beforeCommitOid,
    afterCommitOid: v?.afterCommitOid,
    ref: v?.ref,
    previousTitle: v?.previousTitle,
    newTitle: v?.newTitle,
    stateReason: v?.stateReason,
    lockReason: v?.lockReason,
  }
}

function pullRequestReviewInfoToLegacy(v?: PullRequestReviewInfo) {
  return {
    state: (v?.state ?? '') as PullRequestReviewState,
    reviewId: v && v.reviewId !== 0n ? Number(v.reviewId) : undefined,
    commentCount: v?.commentCount,
    isAuthor: v?.isAuthor,
    qualifiedReviewer: v?.qualifiedReviewer,
    reviewerType: (v?.reviewerType || undefined) as
      | 'vanilla'
      | 'code_scanning'
      | 'copilot'
      | 'dependabot'
      | 'code_quality'
      | 'automated'
      | undefined,
    dismissedState: v?.dismissedState as PullRequestReviewState | undefined,
  }
}
